import { firebase } from '../providers/firebase/firebase';

// Module-level auth readiness gate.
// Registers onAuthStateChanged at import time - before React mounts - so the
// first emission (which may be null during IndexedDB hydration) is captured
// here rather than inside individual checkAuth calls where a premature null
// would cause a spurious logout.
let cachedAuthUser: ReturnType<typeof firebase.auth>['currentUser'] = null;
let authResolved = false;

// Keep the listener active so cachedAuthUser stays current on server-side
// session invalidation (e.g. account disabled, password changed elsewhere).
// The promise only resolves once (on first emission) to gate startup callers.
const authReadyPromise = new Promise<void>((resolve) => {
    firebase.auth().onAuthStateChanged((user) => {
        cachedAuthUser = user;
        if (!authResolved) {
            authResolved = true;
            resolve();
        }
    });
});

/**
 * Wait for Firebase auth to be initialized.
 * On app startup, auth state is restored asynchronously from IndexedDB.
 * This ensures currentUser is available before making authenticated calls.
 * Rejects if no user is signed in after hydration completes.
 */
export async function waitForAuth(): Promise<void> {
    await authReadyPromise;
    if (!cachedAuthUser) {
        throw new Error('Not authenticated');
    }
}

/**
 * Wait for auth hydration and return the Firebase User object (or null).
 * Used by checkAuth to avoid the race in the library's getAuthUser().
 */
export async function waitForAuthUser(): Promise<typeof cachedAuthUser> {
    await authReadyPromise;
    return cachedAuthUser;
}

/**
 * Update the cached auth user after login or logout.
 * Call this after firebase.auth().signInWith*() or signOut() so that
 * waitForAuth/waitForAuthUser reflect the new state immediately without
 * waiting for another onAuthStateChanged cycle.
 */
export function updateAuthUser(user: typeof cachedAuthUser): void {
    cachedAuthUser = user;
}
